import type { StatTile } from "./landing-model";

/**
 * What each slot looks like. One layout, two skins: three bento tiles in both
 * themes, with the same size, padding and number sizes. Slot a is the
 * purple-blue gradient, b the tall card with the accent number, c the blue
 * gradient with a yellow number. The gradients are fills, so they are the same
 * in both themes; the card is white with a 1px border in light and the lit
 * card in Dusk. The yellow is one numeral, never a fill. Slot b is the tall
 * tile, so it gets the larger number. Written out in full so Tailwind finds
 * every class.
 */
const TILE =
  "flex min-h-[9.5rem] min-w-0 flex-col items-stretch justify-end gap-0.5 rounded-[1.25rem] border shadow-lit-card dark:border-edge dark:shadow-lit p-[1.125rem] text-start";
const FILLED = "border-transparent text-white dark:border-lit-edge";
const CARD =
  "border-lit-card-edge bg-card text-foreground dark:border-lit-edge";
const SLOT_STYLE = {
  a: {
    tile: `${TILE} ${FILLED} bg-dusk-chapter-1`,
    value: "text-white",
    number: 4.5,
  },
  b: {
    tile: `${TILE} ${CARD}`,
    value: "text-lit",
    number: 7.5,
  },
  c: {
    tile: `${TILE} ${FILLED} bg-dusk-chapter-2`,
    value: "text-hb-yellow",
    number: 4.5,
  },
} as const;

/**
 * The number's size: the slot's full size where the value is short, smaller as
 * it gets longer so eight characters still fit a half-width tile at 320px. The
 * width is the band's own (container query units), less the gap, the two
 * columns and a tile's padding.
 */
export function statValueSize(value: string, slot: StatTile["slot"]): string {
  const characters = Math.max(1, value.length) * 0.6;
  return `clamp(1.5rem, calc(((100cqi - 0.625rem) / 2 - 2.25rem) / ${characters.toFixed(1)}), ${SLOT_STYLE[slot].number}rem)`;
}

/**
 * The hero's number band as bento tiles. Only the tiles it is given are drawn.
 * The tall middle tile appears only when all three do, so no cell is left
 * empty beside it; two tiles share a row and a single one takes the width.
 */
export function StatBand({ tiles }: { tiles: StatTile[] }) {
  if (tiles.length === 0) return null;
  const full = tiles.length === 3;

  return (
    <div style={{ containerType: "inline-size" }} data-testid="stat-band">
      <ul
        aria-label="The event in numbers"
        className="grid grid-cols-2 gap-2.5"
      >
        {tiles.map((tile) => (
          <li
            key={tile.slot}
            className={`${SLOT_STYLE[tile.slot].tile} ${
              full && tile.slot === "b" ? "row-span-2" : ""
            } ${tiles.length === 1 ? "col-span-2" : ""}`}
            data-testid={`stat-tile-${tile.slot}`}
          >
            <span
              className={`font-bold leading-[0.95] wrap-anywhere ${SLOT_STYLE[tile.slot].value}`}
              style={{ fontSize: statValueSize(tile.value, tile.slot) }}
            >
              {tile.value}
            </span>
            <span className="text-xs font-semibold uppercase tracking-[0.08em] wrap-anywhere">
              {tile.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
